import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { installDemoInterceptor } from "./utils/demoInterceptor";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import InstallPrompt from "./components/InstallPrompt";
import CookieConsent from "./components/CookieConsent";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded pages — only downloaded when navigated to
// Core pages loaded eagerly for instant navigation
import Dashboard from "./pages/Dashboard";
import JobFeed from "./pages/JobFeed";
import SwipeMode from "./pages/SwipeMode";
import CVManager from "./pages/CVManager";
import Applications from "./pages/Applications";
import ProfilePage from "./pages/ProfilePage";

// Secondary pages — lazy loaded
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const InterviewPrepPage = lazy(() => import("./pages/InterviewPrepPage"));
const ExtensionPage = lazy(() => import("./pages/ExtensionPage"));
const CVEditorPage = lazy(() => import("./pages/CVEditorPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const InterviewsPage = lazy(() => import("./pages/InterviewsPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const LandingPreview = lazy(() => import("./pages/LandingPreview"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const FounderUsers = lazy(() => import("./pages/FounderUsers"));
const FounderFunnel = lazy(() => import("./pages/FounderFunnel"));
const DemoLanding = lazy(() => import("./pages/DemoLanding"));
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const OnboardingCV = lazy(() => import("./pages/OnboardingCV"));
const LandingCredit = lazy(() => import("./pages/LandingCredit"));

import { DataCacheProvider } from "./context/DataCacheContext";
import "@/App.css";

const PageLoader = () => (
  <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
    <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Prefetch map — only for lazy-loaded (secondary) pages
const routePrefetchMap = {
  '/interviews': () => import("./pages/InterviewsPage"),
  '/interview-prep': () => import("./pages/InterviewPrepPage"),
  '/subscription': () => import("./pages/SubscriptionPage"),
  '/extension': () => import("./pages/ExtensionPage"),
};

// Preload core routes after login
export const prefetchRoute = (path) => {
  const loader = routePrefetchMap[path];
  if (loader) loader();
};

export const prefetchCoreRoutes = () => {
  // Core pages are eagerly loaded — only prefetch secondary lazy pages
  setTimeout(() => {
    Object.values(routePrefetchMap).forEach(loader => loader());
  }, 2000);
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  
  // Founders ALWAYS have full access — never paywalled
  if (user?.is_founder) return children;

  // Credit-funnel users (freemium) NEVER hit the Stripe paywall — they use
  // their 10 free applications. When those run out we'll gate at the
  // application-submit endpoint, not at the router level.
  if (user?.signup_funnel === 'credit') return children;

  // Paywall: unpaid and expired_trial users can only access subscription, CV upload, and the new signup onboarding flow
  // Allow dashboard access with session_id (returning from Stripe checkout)
  const allowedForUnpaid = ['/subscription', '/cv', '/onboarding'];
  const searchParams = new URLSearchParams(location.search);
  const hasSessionId = searchParams.get('session_id');
  if ((user?.subscription_plan === 'unpaid' || user?.subscription_plan === 'expired_trial') 
    && !allowedForUnpaid.some(p => location.pathname.startsWith(p))
    && !(location.pathname === '/dashboard' && hasSessionId)) {
    return <Navigate to="/subscription?setup=true" replace />;
  }
  
  return children;
};

const FounderRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user?.is_founder) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Default redirect targets /dashboard, BUT if the user arrived via the
    // trial-signup funnel (mode=signup&next=trial) we send them into the
    // CV-upload onboarding step instead. Without this PublicRoute would
    // race AuthPage.handleSubmit's navigate('/onboarding/cv') and clobber it.
    const explicit = searchParams.get('redirect');
    const funnel = (searchParams.get('funnel') || '').toLowerCase();
    const isTrialSignup = searchParams.get('next') === 'trial' || searchParams.get('mode') === 'signup';
    // Credit-funnel users (already logged in, clicking /credit CTA) must skip
    // the /onboarding/cv → Stripe path entirely. Send them straight into the app.
    let redirectTo;
    if (explicit) {
      redirectTo = explicit;
    } else if (funnel === 'credit') {
      redirectTo = '/swipe';
    } else if (isTrialSignup) {
      redirectTo = '/onboarding/cv';
    } else {
      redirectTo = '/dashboard';
    }
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
}

function AppRoutes() {
  return (
    <>
      <PageViewTracker />
      <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/credit" element={<LandingCredit />} />
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/pitch" element={<Suspense fallback={<div className="min-h-screen bg-[#09090b]" />}><PitchDeck /></Suspense>} />
      <Route path="/preview" element={<LandingPreview />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/founder/users" element={<ProtectedRoute><FounderRoute><FounderUsers /></FounderRoute></ProtectedRoute>} />
      <Route path="/founder/funnel" element={<ProtectedRoute><FounderRoute><FounderFunnel /></FounderRoute></ProtectedRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobFeed /></ProtectedRoute>} />
      <Route path="/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
      <Route path="/cv" element={<ProtectedRoute><CVManager /></ProtectedRoute>} />
      <Route path="/cv/edit/:cvId" element={<ProtectedRoute><CVEditorPage /></ProtectedRoute>} />
      <Route path="/interview-prep" element={<ProtectedRoute><InterviewPrepPage /></ProtectedRoute>} />
      <Route path="/extension" element={<ProtectedRoute><ExtensionPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><FounderRoute><AnalyticsPage /></FounderRoute></ProtectedRoute>} />
      <Route path="/swipe" element={<ProtectedRoute><SwipeMode /></ProtectedRoute>} />
      <Route path="/interviews" element={<ProtectedRoute><InterviewsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/onboarding/cv" element={<ProtectedRoute><OnboardingCV /></ProtectedRoute>} />
      <Route path="/free-invite" element={<Navigate to="/auth?invite=free" replace />} />
      <Route path="/beta" element={<Navigate to="/auth?invite=beta2026" replace />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}

// Install demo mode interceptor if active
installDemoInterceptor();

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <DataCacheProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <InstallPrompt />
            <CookieConsent />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </DataCacheProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
